import React, { useEffect, useState } from "react";
import { api, formatError } from "../api";
import { Plus, X, ClipboardList, Trash2 } from "lucide-react";

const REQ_TYPES = [
  { value: "new_position", label: "New Position" },
  { value: "replacement",  label: "Replacement"  },
  { value: "backfill",     label: "Backfill"     },
];
const JOB_TYPES = [
  { value: "full_time",  label: "Full Time"  },
  { value: "internship", label: "Internship" },
  { value: "contract",   label: "Contract"   },
  { value: "part_time",  label: "Part Time"  },
];
const EXP_LEVELS = [
  { value: "entry",     label: "Entry-level"        },
  { value: "mid",       label: "Mid-level (1-3 yrs)"},
  { value: "senior",    label: "Senior (3-6 yrs)"   },
  { value: "lead",      label: "Lead (6-10 yrs)"    },
  { value: "executive", label: "Executive (10+ yrs)"},
];
const QUALIFICATIONS = ["10th", "12th", "Diploma", "Bachelor's", "Master's", "PhD"];
const ASSETS_SUGGEST   = ["Laptop", "Phone", "Car / Bike", "ID badge", "Travel allowance"];
const INDUSTRIES       = ["IT Services", "Manufacturing", "Healthcare", "Education", "FinTech", "EdTech", "Logistics", "Energy"];
const JOB_FUNCTIONS    = ["Engineering", "Sales", "Marketing", "HR", "Finance", "Operations", "Customer Success", "Product"];

const EMPTY = {
  requisition_type: "new_position", job_title: "", job_type: "full_time",
  experience_level: "mid", department: "", designation: "",
  min_qualification: "Bachelor's", ctc_min: "", ctc_max: "", reporting_to: "",
  tat_period: "30 days", preferred_industries: [], preferred_job_functions: [],
  required_company_assets: [], notes: "", requisition_reason: "",
};

function Chip({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-3 py-1.5 rounded-full border-2 border-dashed text-xs font-semibold transition-colors ${
        active ? "bg-signal/10 border-signal text-signal" : "border-rule text-muted hover:border-signal hover:text-signal"
      }`}>{children}</button>
  );
}

function ChipsField({ label, value, onChange, suggestions, testid }) {
  return (
    <div>
      <label className="eyebrow">{label}</label>
      <div className="flex flex-wrap gap-2 mt-2" data-testid={testid}>
        {suggestions.map((s) => {
          const on = value.includes(s);
          return (
            <Chip key={s} active={on}
              onClick={() => onChange(on ? value.filter((x) => x !== s) : [...value, s])}>
              {on ? <X size={11} className="inline mr-1" /> : <Plus size={11} className="inline mr-1" />}{s}
            </Chip>
          );
        })}
      </div>
    </div>
  );
}

function NewRequisitionDialog({ onClose, onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setMsg("");
    try {
      const payload = {
        ...f,
        ctc_min: f.ctc_min === "" ? null : Number(f.ctc_min),
        ctc_max: f.ctc_max === "" ? null : Number(f.ctc_max),
      };
      const r = await api.post("/requisitions", payload);
      onCreated?.(r.data);
      onClose();
    } catch (e2) { setMsg(formatError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto"
            data-testid="new-requisition-dialog">
        <div className="sticky top-0 bg-white px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--rule)" }}>
          <div>
            <h2 className="font-display font-black text-xl">New Requisition</h2>
            <p className="text-xs text-muted">Internal hiring request — once approved, becomes a public job posting.</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" className="btn-ghost" onClick={onClose} data-testid="req-cancel">Back to list</button>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="req-submit">{busy ? "Saving…" : "Submit"}</button>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          <section className="space-y-4">
            <div className="font-display font-bold border-b pb-2" style={{ borderColor: "var(--rule)" }}>Job Details</div>
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <label className="eyebrow">Requisition Type<span className="text-red-500">*</span></label>
                <select required className="input mt-2" value={f.requisition_type} onChange={(e) => set("requisition_type", e.target.value)} data-testid="req-type">
                  {REQ_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="eyebrow">Job Title<span className="text-red-500">*</span></label>
                <input required className="input mt-2" placeholder="e.g. Senior ML Engineer" value={f.job_title} onChange={(e) => set("job_title", e.target.value)} data-testid="req-title" />
              </div>
              <div>
                <label className="eyebrow">Job Type<span className="text-red-500">*</span></label>
                <select required className="input mt-2" value={f.job_type} onChange={(e) => set("job_type", e.target.value)} data-testid="req-job-type">
                  {JOB_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="eyebrow">Experience Level<span className="text-red-500">*</span></label>
                <select required className="input mt-2" value={f.experience_level} onChange={(e) => set("experience_level", e.target.value)} data-testid="req-exp">
                  {EXP_LEVELS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <label className="eyebrow">Department<span className="text-red-500">*</span></label>
                <input required className="input mt-2" placeholder="Engineering" value={f.department} onChange={(e) => set("department", e.target.value)} data-testid="req-dept" />
              </div>
              <div>
                <label className="eyebrow">Designation</label>
                <input className="input mt-2" placeholder="Manager / Engineer / …" value={f.designation} onChange={(e) => set("designation", e.target.value)} data-testid="req-designation" />
              </div>
              <div>
                <label className="eyebrow">Minimum Qualification<span className="text-red-500">*</span></label>
                <select required className="input mt-2" value={f.min_qualification} onChange={(e) => set("min_qualification", e.target.value)} data-testid="req-qual">
                  {QUALIFICATIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="eyebrow">CTC (LPA)</label>
                <div className="flex gap-2 mt-2">
                  <input type="number" min="0" step="0.5" className="input" placeholder="Min" value={f.ctc_min} onChange={(e) => set("ctc_min", e.target.value)} data-testid="req-ctc-min" />
                  <input type="number" min="0" step="0.5" className="input" placeholder="Max" value={f.ctc_max} onChange={(e) => set("ctc_max", e.target.value)} data-testid="req-ctc-max" />
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="eyebrow">Reporting To</label>
                <input className="input mt-2" placeholder="Employee Name" value={f.reporting_to} onChange={(e) => set("reporting_to", e.target.value)} data-testid="req-report-to" />
              </div>
              <div>
                <label className="eyebrow">TAT Period</label>
                <select className="input mt-2" value={f.tat_period} onChange={(e) => set("tat_period", e.target.value)} data-testid="req-tat">
                  {["15 days", "30 days", "45 days", "60 days", "90 days"].map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <ChipsField label="Preferred Industries" value={f.preferred_industries}
              onChange={(v) => set("preferred_industries", v)} suggestions={INDUSTRIES} testid="req-industries" />
            <ChipsField label="Preferred Job Functions" value={f.preferred_job_functions}
              onChange={(v) => set("preferred_job_functions", v)} suggestions={JOB_FUNCTIONS} testid="req-functions" />
            <ChipsField label="Required Company Assets" value={f.required_company_assets}
              onChange={(v) => set("required_company_assets", v)} suggestions={ASSETS_SUGGEST} testid="req-assets" />

            <div>
              <label className="eyebrow">Note / Remarks</label>
              <textarea className="input mt-2 min-h-[80px]" value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="req-notes" />
            </div>
            <div>
              <label className="eyebrow">Requisition Reason<span className="text-red-500">*</span></label>
              <textarea required className="input mt-2 min-h-[100px]"
                placeholder="Why is this hire needed? What's the business impact?"
                value={f.requisition_reason} onChange={(e) => set("requisition_reason", e.target.value)} data-testid="req-reason" />
            </div>
          </section>

          {msg && <div className="text-sm text-red-600" data-testid="req-msg">{msg}</div>}
        </div>
      </form>
    </div>
  );
}

export default function CompanyRequisitions() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    try { const r = await api.get("/requisitions"); setRows(r.data); }
    catch (e) { setErr(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this requisition?")) return;
    try { await api.delete(`/requisitions/${id}`); load(); }
    catch (e) { setErr(formatError(e)); }
  };

  const decide = async (id, step, decision) => {
    const reason = decision !== "approve"
      ? window.prompt(`Reason for ${decision.replace("_", " ")}?`) : "";
    if (decision !== "approve" && !reason) return;
    try { await api.post(`/requisitions/${id}/decide`, { step, decision, reason }); load(); }
    catch (e) { setErr(formatError(e)); }
  };

  const promote = async (id) => {
    if (!window.confirm("Promote this approved requisition to a public job posting?")) return;
    try {
      const r = await api.post(`/requisitions/${id}/promote-to-posting`);
      window.alert(`Posted: ${r.data.title}`); load();
    } catch (e) { setErr(formatError(e)); }
  };

  // Approval chain order — used to label the next-step button.
  const CHAIN = ["department_head", "hr", "finance"];
  const nextStep = (r) => CHAIN[(r.approval_history || []).length];

  return (
    <div className="space-y-6" data-testid="requisitions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="eyebrow flex items-center gap-2"><ClipboardList size={12} /> Requisitions</span>
          <h2 className="font-display font-black text-2xl mt-1">Hiring requests</h2>
          <p className="text-muted text-sm">Internal requests that become public job postings once approved.</p>
        </div>
        <button className="btn-primary" onClick={() => setShowNew(true)} data-testid="new-req-btn">
          <Plus size={14} /> New Requisition
        </button>
      </div>

      {err && <div className="card card-pad text-red-600">{err}</div>}

      <div className="card overflow-x-auto">
        <table className="dense">
          <thead>
            <tr>
              <th>Code</th><th>Title</th><th>Type</th><th>Department</th>
              <th>Experience</th><th>CTC (LPA)</th><th>Status</th><th>Created</th><th />
            </tr>
          </thead>
          <tbody data-testid="requisitions-tbody">
            {rows === null ? (
              <tr><td colSpan={9} className="text-muted">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="text-muted">No requisitions yet. Click <b>New Requisition</b> to add the first one.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`req-row-${r.code}`}>
                <td className="font-mono text-xs">{r.code}</td>
                <td className="font-semibold">{r.job_title}</td>
                <td className="text-muted text-xs">{r.requisition_type?.replace("_", " ")}</td>
                <td>{r.department || "—"}</td>
                <td className="text-muted text-xs">{r.experience_level}</td>
                <td>{r.ctc_min || r.ctc_max ? `${r.ctc_min ?? "—"} – ${r.ctc_max ?? "—"}` : "—"}</td>
                <td><span className="badge">{r.status}</span></td>
                <td className="text-muted text-xs">{String(r.created_at).slice(0, 10)}</td>
                <td>
                  {r.status === "open" || r.status === "in_review" ? (
                    <>
                      <button onClick={() => decide(r.id, nextStep(r), "approve")} className="text-emerald-600 hover:underline text-xs font-semibold mr-2" data-testid={`req-approve-${r.code}`}>Approve&nbsp;{nextStep(r)?.replace("_", " ")}</button>
                      <button onClick={() => decide(r.id, nextStep(r), "reject")} className="text-red-500 hover:underline text-xs mr-2" data-testid={`req-reject-${r.code}`}>Reject</button>
                    </>
                  ) : r.status === "approved" && !r.promoted_posting_id ? (
                    <button onClick={() => promote(r.id)} className="text-signal hover:underline text-xs font-semibold mr-2" data-testid={`req-promote-${r.code}`}>Promote → Posting</button>
                  ) : r.promoted_posting_id ? (
                    <span className="text-xs text-emerald-600 mr-2" data-testid={`req-promoted-${r.code}`}>✓ Posted</span>
                  ) : null}
                  <button onClick={() => del(r.id)} className="text-red-500 hover:text-red-700" data-testid={`req-delete-${r.code}`} aria-label="Delete">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && <NewRequisitionDialog onClose={() => setShowNew(false)} onCreated={load} />}
    </div>
  );
}
